/***
 *
 *   NOTIFICATION
 *   Create a notification anytime using context.notification.show().
 *
 *   The `toast` format matches ui-reference/src/components/ui/sonner.tsx —
 *   top-right, a surface card with a status-coloured left rule. The
 *   full-width `banner` format predates the reference and is kept for the call
 *   sites that use it.
 *
 *   PROPS
 *   message: string containing the message
 *   type: success/error/warning/info
 *   format: 'toast' (default shape in the reference) or undefined for a banner
 *   autoclose: if false, the notification stays until dismissed
 *
 **********/

import { useContext, useEffect, useState } from 'react';
import { ViewContext, Icon, cn } from 'components/lib';
import Style from './notification.tailwind.js';

const ICONS = {
    success: 'check-circle',
    warning: 'alert-triangle',
    error: 'alert-octagon',
    info: 'info',
};

export function Notification(props) {
    const context = useContext(ViewContext);
    const data = context.notification.data;
    const type = props.type || 'info';
    const isToast = props.format === 'toast';

    // Mount off-screen, then transition in on the next frame — CSSTransition's
    // appear classes did this before.
    const [entered, setEntered] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(id);
    }, []);

    const capitalised = type.charAt(0).toUpperCase() + type.slice(1);

    const style = cn(
        isToast ? Style.toast : Style.banner,
        isToast ? Style[`toast${capitalised}`] : Style[`banner${capitalised}`],
        entered ? (isToast ? Style.toastEnter : Style.bannerEnter) : isToast ? Style.toastExit : Style.bannerExit
    );

    return (
        <div className={style} role="status" aria-live="polite">
            <Icon
                size={isToast ? 18 : 19}
                color="currentColor"
                image={data.icon || ICONS[type]}
                className={Style.icon}
            />

            <p className={Style.text}>{data.text}</p>

            {!data.autoclose && (
                <button
                    type="button"
                    className={Style.close}
                    onClick={context.notification.hide}
                    aria-label="Dismiss">
                    <Icon image="x" size={16} color="currentColor" />
                </button>
            )}
        </div>
    );
}
